import { DEFAULT_LANGUAGE } from "./constants";
export class ApiRequestError extends Error {
    constructor(message, status, code) {
        super(message);
        this.status = status;
        this.code = code;
        this.name = "ApiRequestError";
    }
}
function parseApiError(body, fallback) {
    if (!body || typeof body !== "object")
        return { message: fallback };
    const record = body;
    const rawMessage = record.message;
    const message = Array.isArray(rawMessage)
        ? rawMessage.join(", ")
        : typeof rawMessage === "string" && rawMessage.length > 0
            ? rawMessage
            : fallback;
    const code = record.error === "EMAIL_NOT_VERIFIED"
        ? "EMAIL_NOT_VERIFIED"
        : undefined;
    return { message, code };
}
async function parseResponseBody(res) {
    if (res.status === 204) {
        return undefined;
    }
    const text = await res.text();
    if (!text.trim()) {
        return undefined;
    }
    return JSON.parse(text);
}
export function createApiClient(config) {
    const { apiBase, publicMediaBase = apiBase, getToken, getRefreshToken, setTokens, getLanguage = () => DEFAULT_LANGUAGE, onSessionExpired, onSessionRestored, credentials, authMode = "token", refreshPath = "/api/auth/refresh", } = config;
    let refreshInFlight = null;
    let sessionInvalidated = false;
    function markSessionRestored() {
        sessionInvalidated = false;
        onSessionRestored?.();
    }
    async function tryRefreshTokens() {
        if (sessionInvalidated)
            return false;
        if (refreshInFlight)
            return refreshInFlight;
        refreshInFlight = (async () => {
            if (authMode === "cookie") {
                try {
                    const refreshRes = await fetch(refreshPath, {
                        method: "POST",
                        credentials: "include",
                    });
                    if (!refreshRes.ok) {
                        sessionInvalidated = true;
                        onSessionExpired?.();
                        return false;
                    }
                    markSessionRestored();
                    return true;
                }
                catch {
                    return false;
                }
            }
            const refreshToken = getRefreshToken?.();
            if (!refreshToken) {
                sessionInvalidated = true;
                onSessionExpired?.();
                return false;
            }
            try {
                const refreshRes = await fetch(`${apiBase}/auth/refresh`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ refreshToken }),
                    ...(credentials !== undefined ? { credentials } : {}),
                });
                if (!refreshRes.ok) {
                    sessionInvalidated = true;
                    onSessionExpired?.();
                    return false;
                }
                const { accessToken, refreshToken: newRefreshToken } = await refreshRes.json();
                setTokens?.(accessToken, newRefreshToken);
                markSessionRestored();
                return true;
            }
            catch {
                return false;
            }
        })().finally(() => {
            refreshInFlight = null;
        });
        return refreshInFlight;
    }
    async function request(path, options = {}) {
        const { guestSafe = false, ...fetchOptions } = options;
        const token = authMode === "cookie" ? null : (getToken?.() ?? null);
        const headers = {
            ...fetchOptions.headers,
        };
        if (!(fetchOptions.body instanceof FormData)) {
            headers["Content-Type"] = "application/json";
        }
        headers["x-language"] = getLanguage();
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
        const fetchInit = {
            ...fetchOptions,
            headers,
        };
        if (credentials !== undefined) {
            fetchInit.credentials = credentials;
        }
        const requestTimeoutMs = 15000;
        async function fetchWithTimeout(url, init) {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), requestTimeoutMs);
            const priorSignal = init.signal;
            if (priorSignal) {
                if (priorSignal.aborted) {
                    controller.abort();
                }
                else {
                    priorSignal.addEventListener("abort", () => controller.abort(), {
                        once: true,
                    });
                }
            }
            try {
                return await fetch(url, { ...init, signal: controller.signal });
            }
            catch (error) {
                if (error instanceof Error && error.name === "AbortError") {
                    throw new Error(`Request timed out after ${requestTimeoutMs / 1000}s — check API URL and that the backend is running.`);
                }
                throw error;
            }
            finally {
                clearTimeout(timeoutId);
            }
        }
        let res = await fetchWithTimeout(`${apiBase}${path}`, fetchInit);
        if (res.status === 401) {
            const refreshed = await tryRefreshTokens();
            if (refreshed) {
                const nextToken = authMode === "cookie" ? null : (getToken?.() ?? null);
                if (nextToken)
                    headers["Authorization"] = `Bearer ${nextToken}`;
                res = await fetchWithTimeout(`${apiBase}${path}`, fetchInit);
            }
            if (res.status === 401) {
                if (!guestSafe) {
                    sessionInvalidated = true;
                    onSessionExpired?.();
                    throw new Error("Session expired");
                }
                throw new ApiRequestError("Unauthorized", 401);
            }
        }
        if (!res.ok) {
            const error = await res.json().catch(() => ({ message: res.statusText }));
            const { message, code } = parseApiError(error, "Request failed");
            if (res.status === 403) {
                if (code === "EMAIL_NOT_VERIFIED") {
                    throw new ApiRequestError(message, res.status, "EMAIL_NOT_VERIFIED");
                }
                if (message.toLowerCase().includes("banned")) {
                    throw new Error("Your account has been banned. Contact support if you believe this is a mistake.");
                }
                if (message.toLowerCase().includes("suspended")) {
                    throw new Error("Your account is suspended. Please try again later or contact support.");
                }
            }
            throw new ApiRequestError(message, res.status, code);
        }
        return parseResponseBody(res);
    }
    const authApi = {
        getStaffMe: () => request("/auth/staff/me"),
        loginStaff: (credentials) => request("/auth/staff/login", {
            method: "POST",
            headers: { "x-auth-delivery": "cookie" },
            body: JSON.stringify(credentials),
        }),
        logout: () => request("/auth/logout", { method: "POST" }),
        refresh: (tokens) => request("/auth/refresh", {
            method: "POST",
            body: JSON.stringify(tokens),
        }),
    };
    const entitiesApi = {
        getSkills: () => request("/skills"),
        getPlatforms: () => request("/platforms"),
        getCommunities: () => request("/communities"),
    };
    const usersApi = {
        update: (id, data) => request(`/users/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        getProfile: (id) => request(`/users/${id}`),
        getMe: () => request("/users/me"),
        getOne: (id) => request(`/users/${id}`),
        discover: (params) => {
            const search = new URLSearchParams();
            if (params.q)
                search.set("q", params.q);
            if (params.skillIds?.length)
                search.set("skillIds", params.skillIds.join(","));
            if (params.platformIds?.length)
                search.set("platformIds", params.platformIds.join(","));
            if (params.communityIds?.length)
                search.set("communityIds", params.communityIds.join(","));
            if (params.page)
                search.set("page", String(params.page));
            if (params.limit)
                search.set("limit", String(params.limit));
            if (params.includeTotal)
                search.set("includeTotal", "true");
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/users/discover${query}`);
        },
        suggested: (params) => {
            const search = new URLSearchParams();
            if (params?.limit)
                search.set("limit", String(params.limit));
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/users/suggested${query}`);
        },
    };
    const profileSectionsApi = {
        list: (section, userId) => request(`/users/${userId}/${section}`, { guestSafe: true }),
        create: (section, userId, data) => request(`/users/${userId}/${section}`, {
            method: "POST",
            body: JSON.stringify(data),
        }),
        update: (section, userId, sectionId, data) => request(`/users/${userId}/${section}/${sectionId}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        remove: (section, userId, sectionId) => request(`/users/${userId}/${section}/${sectionId}`, {
            method: "DELETE",
        }),
    };
    const profileApi = {
        get: (id) => request(`/profile/${id}`, { guestSafe: true }),
        follow: (id) => request(`/profile/${id}/follow`, { method: "POST" }),
        unfollow: (id) => request(`/profile/${id}/follow`, { method: "DELETE" }),
        getFollowers: (id, params) => {
            const search = new URLSearchParams();
            if (params?.page && params.page > 1) {
                search.set("page", String(params.page));
            }
            if (params?.limit)
                search.set("limit", String(params.limit));
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/profile/${id}/followers${query}`, { guestSafe: true });
        },
        getFollowing: (id, params) => {
            const search = new URLSearchParams();
            if (params?.page && params.page > 1) {
                search.set("page", String(params.page));
            }
            if (params?.limit)
                search.set("limit", String(params.limit));
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/profile/${id}/following${query}`, { guestSafe: true });
        },
    };
    const communitiesApi = {
        list: (params) => {
            const search = new URLSearchParams();
            if (params?.page && params.page > 1) {
                search.set("page", String(params.page));
            }
            if (params?.limit)
                search.set("limit", String(params.limit));
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/communities${query}`, {
                guestSafe: true,
            });
        },
        getAll: async () => {
            const items = [];
            let page = 1;
            const limit = 200;
            while (true) {
                const response = await request(`/communities?page=${page}&limit=${limit}`, { guestSafe: true });
                items.push(...response.items);
                if (!response.hasNextPage) {
                    break;
                }
                page += 1;
            }
            return items;
        },
        explore: (params = {}) => {
            const search = new URLSearchParams();
            if (params.query?.trim())
                search.set("query", params.query.trim());
            if (params.category && params.category !== "all") {
                search.set("category", params.category);
            }
            if (params.sort && params.sort !== "trending") {
                search.set("sort", params.sort);
            }
            if (params.membership && params.membership !== "all") {
                search.set("membership", params.membership);
            }
            if (params.page && params.page > 1) {
                search.set("page", String(params.page));
            }
            if (params.limit)
                search.set("limit", String(params.limit));
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/communities/explore${query}`, { guestSafe: true });
        },
        getJoined: () => request("/communities/joined/me"),
        getOne: (id) => request(`/communities/${id}`, { guestSafe: true }),
        getBySlug: (slug) => request(`/communities/slug/${slug}`, { guestSafe: true }),
        getMembers: (communityId, params) => {
            const search = new URLSearchParams();
            const trimmedQuery = params?.q?.trim();
            if (trimmedQuery) {
                search.set("q", trimmedQuery);
            }
            if (params?.page && params.page > 1) {
                search.set("page", String(params.page));
            }
            if (params?.limit)
                search.set("limit", String(params.limit));
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/communities/${communityId}/members${query}`);
        },
        join: (communityId, userId) => request(`/communities/${communityId}/join`, {
            method: "POST",
            body: JSON.stringify({ userId }),
        }),
        uploadLogo: (communityId, file) => {
            const formData = new FormData();
            formData.append("file", file);
            return request(`/communities/${communityId}/logo`, {
                method: "PATCH",
                body: formData,
            });
        },
        uploadCover: (communityId, file) => {
            const formData = new FormData();
            formData.append("file", file);
            return request(`/communities/${communityId}/cover`, {
                method: "PATCH",
                body: formData,
            });
        },
    };
    const postsApi = {
        getFeed: (params) => {
            const search = new URLSearchParams();
            if (params.communityId)
                search.set("communityId", params.communityId);
            if (params.hashtag)
                search.set("hashtag", params.hashtag);
            if (params.category)
                search.set("category", params.category);
            if (params.sort === "recent")
                search.set("sort", "recent");
            if (params.limit)
                search.set("limit", String(params.limit));
            if (params.cursor)
                search.set("cursor", params.cursor);
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/posts${query}`, {
                guestSafe: true,
            });
        },
        getOne: (id) => request(`/posts/${id}`, { guestSafe: true }),
        create: (data) => request("/posts", {
            method: "POST",
            body: JSON.stringify(data),
        }),
        update: (id, data) => request(`/posts/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        setPinned: (id, data) => request(`/posts/${id}/pin`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        remove: (id) => request(`/posts/${id}`, {
            method: "DELETE",
        }),
        toggleLike: (id) => request(`/posts/${id}/like`, {
            method: "POST",
        }),
        toggleRepost: (id) => request(`/posts/${id}/repost`, {
            method: "POST",
        }),
        votePoll: (id, optionId) => request(`/posts/${id}/poll/vote`, {
            method: "POST",
            body: JSON.stringify({ optionId }),
        }),
        getLikes: (id, params) => {
            const search = new URLSearchParams();
            if (params?.page)
                search.set("page", String(params.page));
            if (params?.limit)
                search.set("limit", String(params.limit));
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/posts/${id}/likes${query}`, { guestSafe: true });
        },
        getReposts: (id, params) => {
            const search = new URLSearchParams();
            if (params?.page)
                search.set("page", String(params.page));
            if (params?.limit)
                search.set("limit", String(params.limit));
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/posts/${id}/reposts${query}`, { guestSafe: true });
        },
        getQuotes: (id, params) => {
            const search = new URLSearchParams();
            if (params?.limit)
                search.set("limit", String(params.limit));
            if (params?.cursor)
                search.set("cursor", params.cursor);
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/posts/${id}/quotes${query}`, {
                guestSafe: true,
            });
        },
    };
    const commentsApi = {
        getByPost: (postId, params) => {
            const search = new URLSearchParams({ postId });
            if (params?.cursor)
                search.set("cursor", params.cursor);
            if (params?.limit)
                search.set("limit", String(params.limit));
            return request(`/comments?${search.toString()}`, {
                guestSafe: true,
            });
        },
        create: (data) => request("/comments", {
            method: "POST",
            body: JSON.stringify(data),
        }),
        remove: (id) => request(`/comments/${id}`, {
            method: "DELETE",
        }),
        toggleLike: (id) => request(`/comments/${id}/like`, {
            method: "POST",
        }),
    };
    const chatApi = {
        getChannels: (communityId) => request(`/chat/channels?communityId=${communityId}`, {
            guestSafe: true,
        }),
        getJoinedChannels: (communityId) => request(communityId
            ? `/chat/channels/joined?communityId=${communityId}`
            : "/chat/channels/joined"),
        getChannel: (id) => request(`/chat/channels/${id}`, {
            guestSafe: true,
        }),
        createChannel: (data) => request("/chat/channels", {
            method: "POST",
            body: JSON.stringify(data),
        }),
        updateChannel: (id, data) => request(`/chat/channels/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        deleteChannel: (id) => request(`/chat/channels/${id}`, {
            method: "DELETE",
        }),
        joinChannel: (channelId) => request(`/chat/channels/${channelId}/join`, {
            method: "POST",
        }),
        joinChannelsBulk: (channelIds) => request("/chat/channels/join-bulk", {
            method: "POST",
            body: JSON.stringify({ channelIds }),
        }),
        getChannelMessages: (channelId, limit = 50, before, beforeId) => {
            const params = new URLSearchParams({ limit: String(limit) });
            if (before)
                params.set("before", before);
            if (beforeId)
                params.set("beforeId", beforeId);
            return request(`/chat/channels/${channelId}/messages?${params.toString()}`);
        },
        searchChannelMessages: (channelId, q, limit = 20, offset = 0) => {
            const params = new URLSearchParams({
                q,
                limit: String(limit),
                offset: String(offset),
            });
            return request(`/chat/channels/${channelId}/search?${params.toString()}`);
        },
        getChannelMedia: (channelId, type, limit = 30, before, beforeId) => {
            const params = new URLSearchParams({
                type,
                limit: String(limit),
            });
            if (before)
                params.set("before", before);
            if (beforeId)
                params.set("beforeId", beforeId);
            return request(`/chat/channels/${channelId}/media?${params.toString()}`);
        },
        sendChannelMessage: (channelId, data) => request(`/chat/channels/${channelId}/messages`, {
            method: "POST",
            body: JSON.stringify(data),
        }),
        editChannelMessage: (messageId, content) => request(`/chat/messages/${messageId}`, {
            method: "PATCH",
            body: JSON.stringify({ content }),
        }),
        deleteChannelMessage: (messageId) => request(`/chat/messages/${messageId}`, { method: "DELETE" }),
        toggleChannelMessageReaction: (messageId, emoji) => request(`/chat/messages/${messageId}/reactions`, {
            method: "POST",
            body: JSON.stringify({ emoji }),
        }),
        getChannelMessageReactionDetails: (messageId) => request(`/chat/messages/${messageId}/reactions`),
        getDmConversations: () => request("/chat/dm/conversations"),
        getUnreadDmCount: () => request("/chat/dm/unread-count"),
        createDmConversation: (receiverId) => request("/chat/dm/conversations", {
            method: "POST",
            body: JSON.stringify({ receiverId }),
        }),
        getDmMessages: (conversationId, limit = 50, before, beforeId) => {
            const params = new URLSearchParams({ limit: String(limit) });
            if (before)
                params.set("before", before);
            if (beforeId)
                params.set("beforeId", beforeId);
            return request(`/chat/dm/conversations/${conversationId}/messages?${params.toString()}`);
        },
        searchConversationMessages: (conversationId, q, limit = 20, offset = 0) => {
            const params = new URLSearchParams({
                q,
                limit: String(limit),
                offset: String(offset),
            });
            return request(`/chat/dm/conversations/${conversationId}/search?${params.toString()}`);
        },
        getConversationMedia: (conversationId, type, limit = 30, before, beforeId) => {
            const params = new URLSearchParams({
                type,
                limit: String(limit),
            });
            if (before)
                params.set("before", before);
            if (beforeId)
                params.set("beforeId", beforeId);
            return request(`/chat/dm/conversations/${conversationId}/media?${params.toString()}`);
        },
        sendDm: (data) => request("/chat/dm", {
            method: "POST",
            body: JSON.stringify(data),
        }),
        markConversationRead: (conversationId) => request(`/chat/dm/conversations/${conversationId}/read`, {
            method: "PATCH",
        }),
        setConversationNotificationsMuted: (conversationId, muted) => request(`/chat/dm/conversations/${conversationId}/notifications`, {
            method: "PATCH",
            body: JSON.stringify({ muted }),
        }),
        editDmMessage: (messageId, content) => request(`/chat/dm/messages/${messageId}`, {
            method: "PATCH",
            body: JSON.stringify({ content }),
        }),
        deleteDmMessage: (messageId) => request(`/chat/dm/messages/${messageId}`, { method: "DELETE" }),
        toggleDmMessageReaction: (messageId, emoji) => request(`/chat/dm/messages/${messageId}/reactions`, {
            method: "POST",
            body: JSON.stringify({ emoji }),
        }),
        getDmMessageReactionDetails: (messageId) => request(`/chat/dm/messages/${messageId}/reactions`),
    };
    const mediaApi = {
        getByResource: (resourceType, resourceId) => request(`/media/resource/${resourceType}/${resourceId}`),
        upload: (file, resourceType, resourceId) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("resourceType", resourceType);
            formData.append("resourceId", resourceId);
            return request("/media/upload", {
                method: "POST",
                body: formData,
            });
        },
        delete: (id) => request(`/media/${id}`, {
            method: "DELETE",
        }),
    };
    const moderationApi = {
        createReport: (data) => request("/moderation/reports", {
            method: "POST",
            body: JSON.stringify(data),
        }),
    };
    const notificationsApi = {
        list: (params = {}) => {
            const search = new URLSearchParams();
            if (params.page)
                search.set("page", String(params.page));
            if (params.limit)
                search.set("limit", String(params.limit));
            if (params.type)
                search.set("type", params.type);
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/notifications${query}`);
        },
        unreadCount: () => request("/notifications/unread-count"),
        markSeen: () => request("/notifications/seen", {
            method: "PATCH",
        }),
        markRead: (id) => request(`/notifications/${id}/read`, {
            method: "PATCH",
        }),
        markAllRead: () => request("/notifications/read-all", {
            method: "PATCH",
        }),
        remove: (id) => request(`/notifications/${id}`, {
            method: "DELETE",
        }),
    };
    const bookmarksApi = {
        create: (postId) => request("/bookmarks", {
            method: "POST",
            body: JSON.stringify({ postId }),
        }),
        remove: (postId) => request(`/bookmarks/${postId}`, {
            method: "DELETE",
        }),
        list: (params = {}) => {
            const search = new URLSearchParams();
            if (params.page)
                search.set("page", String(params.page));
            if (params.limit)
                search.set("limit", String(params.limit));
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/bookmarks${query}`);
        },
        getStatus: (postId) => request(`/bookmarks/${postId}/status`),
    };
    function resolveCommunityMediaUrl(raw) {
        if (!raw)
            return null;
        if (raw.startsWith("http"))
            return raw;
        const base = raw.startsWith("/uploads/") ? publicMediaBase : apiBase;
        return `${base}${raw}`;
    }
    function communityLogoUrl(community) {
        return resolveCommunityMediaUrl(community.logoUrl);
    }
    function communityCoverUrl(community) {
        return resolveCommunityMediaUrl(community.coverUrl);
    }
    function toQueryString(params) {
        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== "") {
                search.set(key, String(value));
            }
        }
        const query = search.toString();
        return query ? `?${query}` : "";
    }
    const adminApi = {
        getDashboardStats: (params = {}) => request(`/admin/dashboard/stats${toQueryString({ from: params.from, to: params.to })}`),
        getDashboardActivity: () => request("/admin/dashboard/activity"),
        listUsers: (params = {}) => request(`/admin/users${toQueryString({
            page: params.page,
            limit: params.limit,
            search: params.search,
            status: params.status,
        })}`),
        getUser: (id) => request(`/admin/users/${id}`),
        updateUser: (id, data) => request(`/admin/users/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        listStaff: (params = {}) => request(`/admin/staff${toQueryString({
            page: params.page,
            limit: params.limit,
            search: params.search,
            role: params.role,
            status: params.status,
        })}`),
        getStaff: (id) => request(`/admin/staff/${id}`),
        createStaff: (data) => request("/admin/staff", {
            method: "POST",
            body: JSON.stringify(data),
        }),
        updateStaff: (id, data) => request(`/admin/staff/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        listReports: (params = {}) => request(`/admin/reports${toQueryString({
            page: params.page,
            limit: params.limit,
            status: params.status,
            targetType: params.targetType,
            from: params.from,
            to: params.to,
        })}`),
        getReport: (id) => request(`/admin/reports/${id}`),
        updateReport: (id, data) => request(`/admin/reports/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        listCommunities: (params = {}) => request(`/admin/communities${toQueryString({
            page: params.page,
            limit: params.limit,
            search: params.search,
            isActive: params.isActive,
        })}`),
        createCommunity: (data) => request("/admin/communities", {
            method: "POST",
            body: JSON.stringify(data),
        }),
        updateCommunity: (id, data) => request(`/admin/communities/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        deactivateCommunity: (id) => request(`/admin/communities/${id}`, {
            method: "DELETE",
        }),
        getCommunityMembers: (communityId, params) => {
            const search = new URLSearchParams();
            if (params?.page && params.page > 1) {
                search.set("page", String(params.page));
            }
            if (params?.limit)
                search.set("limit", String(params.limit));
            const query = search.toString() ? `?${search.toString()}` : "";
            return request(`/admin/communities/${communityId}/members${query}`);
        },
        updateCommunityMemberRole: (communityId, userId, data) => request(`/admin/communities/${communityId}/members/${userId}/role`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        addCommunityMember: (communityId, data) => request(`/admin/communities/${communityId}/members`, {
            method: "POST",
            body: JSON.stringify(data),
        }),
        removeCommunityMember: (communityId, userId) => request(`/admin/communities/${communityId}/members/${userId}`, {
            method: "DELETE",
        }),
        listChannels: (params = {}) => request(`/admin/channels${toQueryString({
            page: params.page,
            limit: params.limit,
            communityId: params.communityId,
        })}`),
        createChannel: (data) => request("/admin/channels", {
            method: "POST",
            body: JSON.stringify(data),
        }),
        updateChannel: (id, data) => request(`/admin/channels/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        deleteChannel: (id) => request(`/admin/channels/${id}`, { method: "DELETE" }),
        listSkills: (params = {}) => request(`/admin/skills${toQueryString({
            page: params.page,
            limit: params.limit,
            search: params.search,
        })}`),
        createSkill: (data) => request("/admin/skills", {
            method: "POST",
            body: JSON.stringify(data),
        }),
        updateSkill: (id, data) => request(`/admin/skills/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        deleteSkill: (id) => request(`/admin/skills/${id}`, { method: "DELETE" }),
        listPlatforms: (params = {}) => request(`/admin/platforms${toQueryString({
            page: params.page,
            limit: params.limit,
            search: params.search,
        })}`),
        createPlatform: (data) => request("/admin/platforms", {
            method: "POST",
            body: JSON.stringify(data),
        }),
        updatePlatform: (id, data) => request(`/admin/platforms/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        deletePlatform: (id) => request(`/admin/platforms/${id}`, { method: "DELETE" }),
        listPosts: (params = {}) => request(`/admin/posts${toQueryString({
            page: params.page,
            limit: params.limit,
            search: params.search,
            communityId: params.communityId,
            includeDeleted: params.includeDeleted,
        })}`),
        updatePost: (id, data) => request(`/admin/posts/${id}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        }),
        deletePost: (id) => request(`/admin/posts/${id}`, { method: "DELETE" }),
        restorePost: (id) => request(`/admin/posts/${id}/restore`, { method: "POST" }),
        listComments: (params = {}) => request(`/admin/comments${toQueryString({
            page: params.page,
            limit: params.limit,
            search: params.search,
            postId: params.postId,
            includeDeleted: params.includeDeleted,
        })}`),
        deleteComment: (id) => request(`/admin/comments/${id}`, { method: "DELETE" }),
        restoreComment: (id) => request(`/admin/comments/${id}/restore`, {
            method: "POST",
        }),
        listMessages: (params) => request(`/admin/messages${toQueryString({
            type: params.type,
            page: params.page,
            limit: params.limit,
            search: params.search,
            channelId: params.channelId,
            conversationId: params.conversationId,
            reportedOnly: params.reportedOnly,
        })}`),
        deleteMessage: (id) => request(`/admin/messages/${id}`, { method: "DELETE" }),
        listMedia: (params = {}) => request(`/admin/media${toQueryString({
            page: params.page,
            limit: params.limit,
            resourceType: params.resourceType,
        })}`),
        deleteMedia: (id) => request(`/admin/media/${id}`, { method: "DELETE" }),
        broadcastNotification: (data) => request("/admin/notifications/broadcast", {
            method: "POST",
            body: JSON.stringify(data),
        }),
        listAuditLogs: (params = {}) => request(`/admin/audit-logs${toQueryString({
            page: params.page,
            limit: params.limit,
            action: params.action,
            actorStaffId: params.actorStaffId,
            from: params.from,
            to: params.to,
        })}`),
    };
    const searchApi = {
        search: (params) => {
            const search = new URLSearchParams();
            search.set("q", params.q.trim());
            if (params.types?.length) {
                search.set("types", params.types.join(","));
            }
            if (params.limit) {
                search.set("limit", String(params.limit));
            }
            return request(`/search?${search.toString()}`, {
                guestSafe: true,
            });
        },
    };
    return {
        authApi,
        entitiesApi,
        usersApi,
        profileApi,
        profileSectionsApi,
        communitiesApi,
        postsApi,
        commentsApi,
        chatApi,
        mediaApi,
        moderationApi,
        notificationsApi,
        bookmarksApi,
        adminApi,
        searchApi,
        communityLogoUrl,
        communityCoverUrl,
        request,
    };
}
const defaultConfig = {
    apiBase: "http://localhost:3000",
    getToken: () => null,
    getRefreshToken: () => null,
    setTokens: () => { },
};
let activeClient = createApiClient(defaultConfig);
export function configureApiClient(config) {
    activeClient = createApiClient(config);
}
function getActiveClient() {
    return activeClient;
}
function bindApi(key) {
    return new Proxy({}, {
        get(_target, prop) {
            const api = getActiveClient()[key];
            const value = api[prop];
            return typeof value === "function" ? value.bind(api) : value;
        },
    });
}
export const authApi = bindApi("authApi");
export const entitiesApi = bindApi("entitiesApi");
export const usersApi = bindApi("usersApi");
export const profileApi = bindApi("profileApi");
export const profileSectionsApi = bindApi("profileSectionsApi");
export const communitiesApi = bindApi("communitiesApi");
export const postsApi = bindApi("postsApi");
export const commentsApi = bindApi("commentsApi");
export const chatApi = bindApi("chatApi");
export const mediaApi = bindApi("mediaApi");
export const moderationApi = bindApi("moderationApi");
export const notificationsApi = bindApi("notificationsApi");
export const bookmarksApi = bindApi("bookmarksApi");
export const adminApi = bindApi("adminApi");
export const searchApi = bindApi("searchApi");
export function communityLogoUrl(community) {
    return getActiveClient().communityLogoUrl(community);
}
export function communityCoverUrl(community) {
    return getActiveClient().communityCoverUrl(community);
}
export const API_BASE = defaultConfig.apiBase;
export const LEGAL_VERSION = "1.0-draft";
export { EMPLOYMENT_TYPES } from "./types";
export { POST_CATEGORIES, REPORT_TARGET_TYPES, REPORT_REASONS, NOTIFICATION_TYPES, NOTIFICATION_TARGET_TYPES, } from "./constants";
export { USER_STATUSES, REPORT_STATUSES, ADMIN_MESSAGE_TYPES, BROADCAST_SEGMENTS, MEMBER_ROLES, MEDIA_RESOURCE_TYPES, } from "./types";
//# sourceMappingURL=api.js.map